export class GeneralService {
  public static readonly PROD_HOST = "api.trymaitai.ai";
  public static readonly LOCAL_HOST = "127.0.0.1:5000";

  // Use environment variables to determine the correct backend endpoint
  public static readonly BACKEND_ENDPOINT =
    import.meta.env.VITE_API_URL || `https://${GeneralService.PROD_HOST}`;

  // WebSocket endpoint for Breeze streaming (mirrors maitai_demo)
  public static readonly BREEZE_ENDPOINT =
    import.meta.env.VITE_BREEZE_URL || "wss://api.trymaitai.ai";

  // Transcription service endpoint for Deepgram WebSocket support
  public static readonly TRANSCRIPTION_SERVICE_ENDPOINT =
    import.meta.env.VITE_MAITAI_TRANSCRIPTION_SERVICE ||
    "wss://api.trymaitai.ai";

  public static readonly options: RequestInit = {
    method: "GET",
    mode: "cors" as RequestMode,
    headers: {
      "Content-Type": "application/json",
      "Accept-Charset": "UTF-8",
      "X-Device-Id": "",
      Authorization: "",
    },
  };

  static async handleResponse(response: any, expectedType: any) {
    if (expectedType === JSON) {
      try {
        return await response.json();
      } catch {
        console.error("Error getting JSON from response, getting TEXT instead");
        return response.text();
      }
    } else {
      return response.text();
    }
  }

  public static async postFormData(
    path: string,
    formData: FormData,
    loadingHandler: ((loading: boolean) => void) | null = null
  ) {
    const fullPath = `${GeneralService.BACKEND_ENDPOINT}${path}`;

    if (loadingHandler != null) loadingHandler(true);

    try {
      // Don't set Content-Type header for FormData - let browser set it with boundary
      const options: RequestInit = {
        method: "POST",
        mode: "cors" as RequestMode,
        body: formData,
        headers: {
          "Accept-Charset": "UTF-8",
          "X-Device-Id": "",
          Authorization: "",
        },
      };

      const response = await fetch(fullPath, options);

      if (response.ok) {
        const result = await this.handleResponse(response, JSON);
        if (loadingHandler != null) loadingHandler(false);
        return result;
      } else {
        if (loadingHandler != null) loadingHandler(false);
        const errorText = await response.text();
        throw new Error(
          errorText || `HTTP ${response.status}: ${response.statusText}`
        );
      }
    } catch (error) {
      if (loadingHandler != null) loadingHandler(false);
      throw error;
    }
  }

  public static async post(
    path: string,
    body: any,
    loadingHandler: ((loading: boolean) => void) | null = null,
    expectedType: any = JSON
  ) {
    const fullPath = `${GeneralService.BACKEND_ENDPOINT}${path}`;

    if (loadingHandler != null) loadingHandler(true);

    try {
      const postOptions: RequestInit = {
        ...GeneralService.options,
        method: "POST",
        body: typeof body === "string" ? body : JSON.stringify(body),
      };

      const response = await fetch(fullPath, postOptions);

      if (response.ok) {
        const result = await this.handleResponse(response, expectedType);
        if (loadingHandler != null) loadingHandler(false);
        return result;
      } else {
        if (loadingHandler != null) loadingHandler(false);
        const errorText = await response.text();
        throw new Error(
          errorText || `HTTP ${response.status}: ${response.statusText}`
        );
      }
    } catch (error) {
      if (loadingHandler != null) loadingHandler(false);
      throw error;
    }
  }

  public static async get(
    path: string,
    loadingHandler: ((loading: boolean) => void) | null = null,
    expectedType: any = JSON
  ) {
    const fullPath = `${GeneralService.BACKEND_ENDPOINT}${path}`;

    if (loadingHandler != null) loadingHandler(true);

    try {
      const getOptions: RequestInit = {
        ...GeneralService.options,
        method: "GET",
      };

      const response = await fetch(fullPath, getOptions);

      if (response.ok) {
        const result = await this.handleResponse(response, expectedType);
        if (loadingHandler != null) loadingHandler(false);
        return result;
      } else {
        if (loadingHandler != null) loadingHandler(false);
        const errorText = await response.text();
        throw new Error(
          errorText || `HTTP ${response.status}: ${response.statusText}`
        );
      }
    } catch (error) {
      if (loadingHandler != null) loadingHandler(false);
      throw error;
    }
  }
}

export default GeneralService;
